import React from 'react';

type HTMLTagProps<T extends HTMLElement> = React.HTMLAttributes<T> & {
  className?: string;
};

const H1 = React.forwardRef<HTMLHeadingElement, HTMLTagProps<HTMLHeadingElement>>(
  ({ className = '', children, ...rest }, ref) => (
    <h1 ref={ref} className={`text-4xl font-bold ${className}`} {...rest}>
      {children}
    </h1>
  )
);
H1.displayName = 'AppTypography.H1';

const H2 = React.forwardRef<HTMLHeadingElement, HTMLTagProps<HTMLHeadingElement>>(
  ({ className = '', children, ...rest }, ref) => (
    <h2 ref={ref} className={`text-3xl font-bold ${className}`} {...rest}>
      {children}
    </h2>
  )
);
H2.displayName = 'AppTypography.H2';

const H3 = React.forwardRef<HTMLHeadingElement, HTMLTagProps<HTMLHeadingElement>>(
  ({ className = '', children, ...rest }, ref) => (
    <h3 ref={ref} className={`text-2xl font-bold ${className}`} {...rest}>
      {children}
    </h3>
  )
);
H3.displayName = 'AppTypography.H3';

const H4 = React.forwardRef<HTMLHeadingElement, HTMLTagProps<HTMLHeadingElement>>(
  ({ className = '', children, ...rest }, ref) => (
    <h4 ref={ref} className={`text-xl font-semibold ${className}`} {...rest}>
      {children}
    </h4>
  )
);
H4.displayName = 'AppTypography.H4';

const H5 = React.forwardRef<HTMLHeadingElement, HTMLTagProps<HTMLHeadingElement>>(
  ({ className = '', children, ...rest }, ref) => (
    <h5 ref={ref} className={`text-lg font-medium ${className}`} {...rest}>
      {children}
    </h5>
  )
);
H5.displayName = 'AppTypography.H5';

const P = React.forwardRef<HTMLParagraphElement, HTMLTagProps<HTMLParagraphElement>>(
  ({ className = '', children, ...rest }, ref) => (
    <p ref={ref} className={`text-base ${className}`} {...rest}>
      {children}
    </p>
  )
);
P.displayName = 'AppTypography.P';

const Small = React.forwardRef<HTMLParagraphElement, HTMLTagProps<HTMLParagraphElement>>(
  ({ className = '', children, ...rest }, ref) => (
    <p ref={ref} className={`text-sm text-gray-600 ${className}`} {...rest}>
      {children}
    </p>
  )
);
Small.displayName = 'AppTypography.Small';

const XS = React.forwardRef<HTMLParagraphElement, HTMLTagProps<HTMLParagraphElement>>(
  ({ className = '', children, ...rest }, ref) => (
    <p ref={ref} className={`text-xs text-gray-500 ${className}`} {...rest}>
      {children}
    </p>
  )
);
XS.displayName = 'AppTypography.XS';

const Text = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & { type?: 'secondary' | 'default' }
>(({ className = '', children, type = 'default', ...rest }, ref) => (
  <span
    ref={ref}
    className={`${type === 'secondary' ? 'text-gray-500' : ''} ${className}`}
    {...rest}
  >
    {children}
  </span>
));
Text.displayName = 'AppTypography.Text';

const Link = React.forwardRef<HTMLAnchorElement, React.AnchorHTMLAttributes<HTMLAnchorElement>>(
  ({ className = '', children, ...rest }, ref) => (
    <a ref={ref} className={`hover:underline ${className}`} {...rest}>
      {children}
    </a>
  )
);
Link.displayName = 'AppTypography.Link';

const AppTypography = Object.assign(() => null, {
  H1,
  H2,
  H3,
  H4,
  H5,
  P,
  Small,
  XS,
  Text,
  Link,
});

export default AppTypography;
